import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsRTL } from '@context/SessionSettingsContext'

import { Body1, Body2 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip'
import { CircleMinusIcon } from '@ui/icons/CircleMinus.icon'
import { EditIcon } from '@ui/icons/Edit.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { StarIcon } from '@ui/icons/Star.icon'
import { StarFilledIcon } from '@ui/icons/StarFilled.icon'
import {
  SensitiveTableCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

import { Box } from '@shared/components/Box'

import { useTranslatedRebateOptions } from './useTranslatedRebateOptions'

import styles from './GroupsPageTable.module.scss'

interface GroupsPageTableProps {
  sortOptions?: NameDto<keyof IntroducingBrokerRebateGroupDto | null>[]
  sticky?: 'left'
  onEdit: (group: IntroducingBrokerRebateGroupDto) => void
  onDelete: (group: IntroducingBrokerRebateGroupDto) => void
  tableQuery: PaginatedQueryResult<IntroducingBrokerRebateGroupDto>
  isAnyFilterActive: boolean
  openCreateGroupModal: () => void
}

const DefaultGroupStar = () => {
  const { t } = useTranslation()

  return (
    <Tooltip content={t('IB.RebatesPage.GroupsPage.Default group for clients assignment')}>
      <div className={styles.defaultGroupStar}>
        <span className={styles.starEmpty}>
          <StarIcon size={20} />
        </span>
        <span className={styles.starFilled}>
          <StarFilledIcon size={20} />
        </span>
      </div>
    </Tooltip>
  )
}

export const GroupsPageTable: FC<GroupsPageTableProps> = (props) => {
  const {
    tableQuery,
    sortOptions,
    sticky,
    onEdit,
    onDelete,
    isAnyFilterActive,
    openCreateGroupModal,
  } = props

  const { pageQuery, setPageQuery, data: rebateGroups } = tableQuery
  const isRTL = useIsRTL()
  const getColumnAlign = (id?: keyof IntroducingBrokerRebateGroupDto) => {
    switch (id) {
      case 'rebatePeriod':
      case 'name':
      case 'rebateApprovalType':
        return isRTL ? 'right' : 'left'
      default:
        return isRTL ? 'left' : 'right'
    }
  }

  const { translateApprovalTypes, translatePeriods } = useTranslatedRebateOptions()
  return (
    <Table
      isLoading={tableQuery.isLoading}
      noResults={
        !tableQuery.data.length && (
          <EmptyGroupsPageTable
            onCreateClick={openCreateGroupModal}
            isAnyFilterActive={isAnyFilterActive}
          />
        )
      }
      sticky={sticky}
      className={styles.table}
      hideScrollbarOnMobile
    >
      <TableHeader>
        <TableRow>
          {sortOptions?.map((option) =>
            option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                sort={pageQuery?.sort || undefined}
                sortOrder={pageQuery?.sortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
                align={getColumnAlign(option.id)}
              >
                {option.name}
              </TableHeadSort>
            ) : (
              <TableHead align={isRTL ? 'left' : 'right'} key={option.name}>
                {option.name}
              </TableHead>
            )
          )}
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rebateGroups.map((group) => (
          <TableRow key={group.id}>
            <SensitiveTableCell>
              <Box flex gap='x8' align='center'>
                <Body2>{group.name}</Body2>
                {group.isDefault && <DefaultGroupStar />}
              </Box>
            </SensitiveTableCell>
            <SensitiveTableCell>
              {translateApprovalTypes(group.rebateApprovalType).name}
            </SensitiveTableCell>
            <SensitiveTableCell>{translatePeriods(group.rebatePeriod).name}</SensitiveTableCell>
            <SensitiveTableCell align='right'>{group.tradingAccountsCount}</SensitiveTableCell>
            <SensitiveTableCell align='right'>{group.rebateAmountInPercentage}%</SensitiveTableCell>
            <TableCell hasTruncate={false} align='right' className={styles.rowActions} width={50}>
              <RowActions onEdit={() => onEdit(group)} onDelete={() => onDelete(group)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface RowActionsProps {
  onEdit: () => void
  onDelete: () => void
}

export const RowActions: FC<RowActionsProps> = (props) => {
  const { onEdit, onDelete } = props

  const { t } = useTranslation()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const actions = [
    {
      label: t('Edit'),
      onClick: () => {
        onEdit()
        setIsDropdownOpen(false)
      },
      icon: EditIcon,
    },
    {
      label: t('Delete'),
      onClick: () => {
        onDelete()
        setIsDropdownOpen(false)
      },
      icon: CircleMinusIcon,
    },
  ]

  return (
    <Popover
      direction='topRight'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <PopoverCard>
          <PopoverList>
            {actions?.map((action) => (
              <ListItem
                key={action.label}
                onClick={action.onClick}
                startSlot={{ icon: action.icon }}
              >
                {action.label}
              </ListItem>
            ))}
          </PopoverList>
        </PopoverCard>
      }
    >
      <IconButton.Ghost icon={KebabIcon} />
    </Popover>
  )
}

interface EmptyGroupsPageTableProps {
  onCreateClick: () => void
  isAnyFilterActive?: boolean
}

export const EmptyGroupsPageTable: FC<EmptyGroupsPageTableProps> = (props) => {
  const { onCreateClick, isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <div className={styles.emptyGroupsPageTable}>
      <TableNoResults
        className={styles.infoWrapper}
        title={
          isAnyFilterActive
            ? t('IB.RebatesPage.GroupsPage.Nothing to show')
            : t('IB.RebatesPage.GroupsPage.No groups to display')
        }
        content={
          isAnyFilterActive ? (
            t('IB.RebatesPage.GroupsPage.Please, adjust your filters')
          ) : (
            <Body1>
              {t('IB.RebatesPage.GroupsPage.Create your first group')}{' '}
              <Link onClick={onCreateClick} size='lg' underline>
                <Body1>{t('IB.RebatesPage.GroupsPage.Here')}</Body1>
              </Link>
            </Body1>
          )
        }
      />
    </div>
  )
}
